import React from 'react';
import NProgress from 'nprogress';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { doc, Timestamp, updateDoc } from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/nextjs';
import { Button } from '../../commons';
import { Input, RadioCheckInput } from '../../inputs';
import { firestore, invariant, loadAnalytics } from '../../../lib';
import { Application } from '../../../models';

interface StaticalFormProps {
  application: Application;
  isReadOnly?: boolean;
  statistical: {
    hear: string[];
    other: string;
  };
}

const toastError = (message: string) =>
  toast.error(message, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });

export default function StatisticalForm({
  application,
  statistical,
  isReadOnly = false,
}: StaticalFormProps) {
  const router = useRouter();
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    try {
      if (
        !formData.get('campus')?.toString()?.trim() &&
        !formData.get('newspaper')?.toString()?.trim() &&
        !formData.get('radio')?.toString()?.trim() &&
        !formData.get('previous-scholarship')?.toString()?.trim()
      ) {
        invariant(formData.get('other')?.toString()?.trim(), 'Please specify');
      }
    } catch (err) {
      return toastError(err.message);
    }
    try {
      NProgress.start();
      await updateDoc(doc(firestore, 'applications', application?.id), {
        lastUpdated: Timestamp.fromDate(new Date()),
        step: '9',
        hear: [
          formData.get('campus')?.toString()?.trim(),
          formData.get('newspaper')?.toString()?.trim(),
          formData.get('radio')?.toString()?.trim(),
          formData.get('previous-scholarship')?.toString()?.trim(),
        ].filter((x) => !!x),
        other: formData.get('other')?.toString()?.trim() ?? '',
      });
      logEvent(loadAnalytics(), 'step_completed', {
        step: '9',
        finishedTime: new Date().toISOString(),
      });
      try {
        posthog?.capture(
          'step_completed',
          {
            step: '9',
            finishedTime: new Date().toISOString(),
          },
          { transport: 'sendBeacon' }
        );
      } catch (err) {
        posthog.captureException(err);
      }
      await router.push(
        '/request-scholarship/[...id]',
        '/request-scholarship/10'
      );
    } catch (err) {
      NProgress.done();
      posthog?.captureException(err);
      Sentry.addBreadcrumb({ level: 'error', category: 'step-9' });
      Sentry.captureException(err);
      toastError('An error has occurred, please try again later. CODE: E91');
    }
  };
  return (
    <form className="w-full flex flex-col" method="POST" onSubmit={onSubmit}>
      <p className="text-lg text-neutral-600">
        Where did you hear about our Carreras scholarship?
      </p>
      <RadioCheckInput
        name="campus"
        type="check"
        label="On Campus"
        value="campus"
        labelClassName="font-bold text-[20px] text-neutral-500"
        defaultChecked={statistical.hear.includes('campus')}
        className="mb-2"
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      <RadioCheckInput
        name="newspaper"
        type="check"
        label="Newspaper"
        value="newspaper"
        labelClassName="font-bold text-[20px] text-neutral-500"
        defaultChecked={statistical.hear.includes('newspaper')}
        className="mb-2"
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      <RadioCheckInput
        name="radio"
        type="check"
        label="Radio"
        value="radio"
        labelClassName="font-bold text-[20px] text-neutral-500"
        defaultChecked={statistical.hear.includes('radio')}
        className="mb-2"
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      <RadioCheckInput
        name="previous-scholarship"
        type="check"
        label="Previous scholarship awarded"
        value="previous-scholarship"
        labelClassName="font-bold text-[20px] text-neutral-500"
        defaultChecked={statistical.hear.includes('previous-scholarship')}
        className="mb-2"
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      <div className="flex flex-row">
        <p className="text-xl text-neutral-500 mr-2 my-auto">Other</p>
        <Input
          type="text"
          label=""
          name="other"
          placeholder="Enter option"
          defaultValue={statistical?.other ?? ''}
          readOnly={isReadOnly}
          disabled={isReadOnly}
        />
      </div>
      {isReadOnly ? null : (
        <div className="mt-8 flex">
          <Button type="submit" color="blue" className="ml-auto w-[135px]">
            Continue
          </Button>
        </div>
      )}
    </form>
  );
}
